import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { CrudForm, CrudStyles } from '../shared/Crud';
import MessageModal from '../shared/MessageModal';
import SystemAccessSection from './SystemAccessSection';
import AddressForm from '../shared/AddressForm';
import PhoneNumbersInput from '../shared/PhoneNumbersInput';
import RolesManager from '../shared/RolesDisplay/RolesManager';
import { useForm } from '../../hooks/useForm';
import { useAddress } from '../../hooks/useAddress';
import { usePhoneNumbers } from '../../hooks/usePhoneNumbers';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { maskCPF, maskDate } from '../../utils/masks';
import { isValidEmail, isValidCPF } from '../../utils/validation';
import { addressNameError, mapApiLocationKeepingAddressAlias, normalizeAddressName } from '../../utils/addressName';
import employeeService from '../../services/employeeService';
import addressService from '../../services/addressService';
import userService from '../../services/userService';

const { FormGroup, FormRow, Input, SectionTitle, CheckboxGroup } = CrudStyles;

// Constantes
const ADDRESS_CHECK_DELAY = 200;
const MAX_ADDRESS_CHECK_ATTEMPTS = 5;
const ADDRESS_ERROR_TIMEOUT = 1000;

// Componente de Loading
const LoadingSpinner = () => (
    <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '48px 24px',
        gap: '16px'
    }}>
        <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e0e0e0',
            borderTopColor: '#667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: '#666', fontSize: '14px' }}>
            Carregando dados do colaborador...
        </p>
        <style>{`
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);

export default function EmployeeForm({ initialData, onSubmit, onCancel, selectedCustomerId }) {
    const isEditing = !!initialData?.id;
    const needsAddressLoad = !!(initialData?.id && initialData?.addressId);
    const { isTransportCompany, isManagingOwnData } = useCustomerSelector();
    /** Bloqueio/desbloqueio de login: apenas usuário de empresa de transporte (não cliente/RH externo com colaboradores). */
    const canManageUserBlock = isTransportCompany;
    
    // Campo "É motorista?" só aparece quando é empresa de transporte gerenciando próprios dados
    const canShowDriverField = isTransportCompany && isManagingOwnData;

    // Estados
    const [loadingUserAccessAction, setLoadingUserAccessAction] = useState(null);
    const [userAccessMessageModal, setUserAccessMessageModal] = useState({ open: false, title: 'Aviso', message: '' });
    const [isLoadingData, setIsLoadingData] = useState(needsAddressLoad);
    const [addressLoaded, setAddressLoaded] = useState(!needsAddressLoad);
    
    // Refs
    const prevCustomerIdRef = useRef(selectedCustomerId);
    const addressRef = useRef(null);
    const phoneNumbersRef = useRef(null);

    // Inicializa dados iniciais
    const initialFormData = useMemo(() => ({
        name: initialData?.name || '',
        email: initialData?.email || '',
        document: initialData?.cpf ? maskCPF(initialData.cpf) : '',
        birthDate: initialData?.birthDate 
            ? initialData.birthDate.split('-').reverse().join('/')
            : '',
        jobTitle: initialData?.jobTitle || '',
        requester: initialData?.requester || false,
        driver: initialData?.driver || false,
    }), [initialData]);

    const initialPhones = useMemo(() => 
        initialData?.phoneNumbers && initialData.phoneNumbers.length > 0
            ? initialData.phoneNumbers.map(p => p.number || p)
            : ['']
    , [initialData?.phoneNumbers]);

    const initialAddress = useMemo(() => 
        initialData ? {
            name: initialData.addressName || '',
            zipCode: initialData.zipCode || '',
            street: initialData.street || '',
            number: initialData.number || '',
            complement: initialData.complement || '',
            district: initialData.district || '',
            city: initialData.city || '',
            stateAbbr: initialData.stateAbbr || '',
            stateName: initialData.stateName || '',
            addressId: initialData.addressId || null,
        } : {}
    , [initialData]);

    // Hooks
    const address = useAddress(initialAddress, selectedCustomerId);
    const phoneNumbers = usePhoneNumbers(initialPhones);

    // Atualiza refs quando valores mudam
    useEffect(() => {
        addressRef.current = address.address;
    }, [address.address]);

    useEffect(() => {
        phoneNumbersRef.current = phoneNumbers.phoneNumbers;
    }, [phoneNumbers.phoneNumbers]);

    // Verifica se o endereço foi preenchido após o carregamento (fallback)
    useEffect(() => {
        if (isLoadingData && !addressLoaded && initialData?.id && initialData?.addressId) {
            const hasAddressData = 
                address.address.street && 
                address.address.zipCode && 
                address.address.district && 
                address.address.city && 
                address.address.stateAbbr;
            
            if (hasAddressData) {
                setAddressLoaded(true);
            }
        }
    }, [
        address.address.street, 
        address.address.zipCode, 
        address.address.district, 
        address.address.city, 
        address.address.stateAbbr, 
        isLoadingData, 
        addressLoaded, 
        initialData?.id, 
        initialData?.addressId
    ]);

    // Validação do formulário
    const validateForm = useCallback((data) => {
        const errors = {};
        const currentAddress = addressRef.current;

        if (!data.name?.trim()) {
            errors.name = 'Nome é obrigatório';
        }
        
        if (!data.email?.trim()) {
            errors.email = 'Email é obrigatório';
        } else if (!isValidEmail(data.email)) {
            errors.email = 'Email inválido';
        }
        
        if (data.document?.trim() && !isValidCPF(data.document)) {
            errors.document = 'CPF inválido';
        }
        
        if (data.birthDate && data.birthDate.length < 10) {
            errors.birthDate = 'Data inválida';
        }

        // Validação de endereço
        if (!currentAddress.zipCode) errors.zipCode = 'CEP é obrigatório';
        if (!currentAddress.street) errors.street = 'Rua é obrigatória';
        if (!currentAddress.number) errors.number = 'Número é obrigatório';
        if (!currentAddress.district) errors.district = 'Bairro é obrigatório';
        if (!currentAddress.city) errors.city = 'Cidade é obrigatória';
        if (!currentAddress.stateAbbr) errors.stateAbbr = 'UF é obrigatório';
        const nameError = addressNameError(currentAddress.name);
        if (nameError) errors.addressName = nameError;

        return errors;
    }, []);

    // Preparação do payload para envio
    const preparePayload = useCallback((data) => {
        const payload = {
            id: initialData?.id,
            customerId: selectedCustomerId,
            name: data.name,
            phoneNumbers: phoneNumbersRef.current
                .filter(p => p.trim().length > 0)
                .map(p => ({ number: p })),
            document: data.document?.trim() ? data.document.trim() : null,
            birthDate: data.birthDate ? data.birthDate.split('/').reverse().join('-') : null,
            address: {
                id: !address.isAddressModified ? addressRef.current.addressId : null,
                street: addressRef.current.street,
                number: addressRef.current.number,
                complement: addressRef.current.complement,
                district: addressRef.current.district,
                city: addressRef.current.city,
                name: normalizeAddressName(addressRef.current.name),
                zipCode: addressRef.current.zipCode,
                state: {
                    abbreviation: addressRef.current.stateAbbr,
                    name: addressRef.current.stateName || addressRef.current.stateAbbr,
                }
            },
            jobTitle: data.jobTitle,
        };

        // Email e configurações só são enviados no cadastro (novo colaborador)
        if (!initialData) {
            payload.email = data.email;
            payload.requester = data.requester;
            payload.sendRegistrationNotification = false;
            payload.driver = data.driver;
        }

        return payload;
    }, [initialData, selectedCustomerId, address.isAddressModified]);

    // Hook de formulário
    const apiErrorOptions = useMemo(
        () => ({ mapApiFieldLocation: mapApiLocationKeepingAddressAlias }),
        [],
    );

    const { formData, errors, isSubmitting, setField, setFormData, setErrors, handleSubmit } = useForm(
        initialFormData,
        async (data) => {
            const payload = preparePayload(data);
            await onSubmit(payload);
        },
        validateForm,
        apiErrorOptions,
    );

    // Função auxiliar para verificar se customerId mudou
    const checkCustomerIdChanged = useCallback(() => {
        return prevCustomerIdRef.current !== selectedCustomerId;
    }, [selectedCustomerId]);

    // Função auxiliar para verificar se endereço tem dados válidos
    const hasValidAddressData = useCallback((addr) => {
        return !!(addr?.street && addr?.zipCode && addr?.district && addr?.city && addr?.stateAbbr);
    }, []);

    // Função auxiliar para verificar endereço com retry
    const checkAddressWithRetry = useCallback((attempts = 0) => {
        const currentAddress = address.address;
        const stillHasData = hasValidAddressData(currentAddress);
        
        if (stillHasData) {
            setAddressLoaded(true);
        } else if (attempts < MAX_ADDRESS_CHECK_ATTEMPTS) {
            setTimeout(() => checkAddressWithRetry(attempts + 1), ADDRESS_CHECK_DELAY);
        } else {
            setAddressLoaded(true);
        }
    }, [address.address, hasValidAddressData]);

    // Função para carregar dados do colaborador
    const loadEmployeeDetails = useCallback(async () => {
        if (checkCustomerIdChanged()) {
            setIsLoadingData(false);
            setAddressLoaded(true);
            return;
        }

        setIsLoadingData(true);
        setAddressLoaded(false);
        
        try {
            if (checkCustomerIdChanged()) {
                setIsLoadingData(false);
                setAddressLoaded(true);
                return;
            }
            
            const empData = await employeeService.get(initialData.id, selectedCustomerId);
            
            if (checkCustomerIdChanged()) {
                setIsLoadingData(false);
                setAddressLoaded(true);
                return;
            }

            // Atualiza telefones
            if (empData.phoneNumbers && empData.phoneNumbers.length > 0) {
                phoneNumbers.setPhoneNumbers(
                    empData.phoneNumbers.map(p => p.number)
                );
            }

            // Atualiza endereço se tiver addressId
            if (empData.addressId) {
                try {
                    if (checkCustomerIdChanged()) {
                        setIsLoadingData(false);
                        setAddressLoaded(true);
                        return;
                    }
                    
                    const addressData = await addressService.getById(empData.addressId, selectedCustomerId);
                    
                    if (checkCustomerIdChanged()) {
                        setIsLoadingData(false);
                        setAddressLoaded(true);
                        return;
                    }
                    
                    address.setAddress({
                        name: addressData.name || '',
                        zipCode: addressData.zipCode || '',
                        street: addressData.street || '',
                        number: addressData.number || '',
                        complement: addressData.complement || '',
                        district: addressData.district || '',
                        city: addressData.city || '',
                        stateAbbr: addressData.state?.abbreviation || '',
                        stateName: addressData.state?.name || '',
                        addressId: addressData.id || null,
                    });
                    
                    await new Promise(resolve => setTimeout(resolve, ADDRESS_CHECK_DELAY));
                    
                    if (hasValidAddressData(address.address)) {
                        setAddressLoaded(true);
                    } else {
                        setTimeout(() => checkAddressWithRetry(), ADDRESS_CHECK_DELAY);
                    }
                } catch (addrError) {
                    setTimeout(() => {
                        setAddressLoaded(true);
                    }, ADDRESS_ERROR_TIMEOUT);
                }
            } else {
                setAddressLoaded(true);
            }

            // Atualiza formulário
            setFormData({
                name: empData.name || '',
                email: empData.email || '',
                document: empData.cpf ? maskCPF(empData.cpf) : '',
                birthDate: empData.birthDate 
                    ? empData.birthDate.split('-').reverse().join('/')
                    : '',
                jobTitle: empData.jobTitle || '',
                requester: empData.requester || false,
                driver: empData.driver || false,
            });
        } catch (error) {
            setAddressLoaded(true);
        } finally {
            setIsLoadingData(false);
        }
    }, [
        initialData?.id, 
        selectedCustomerId, 
        checkCustomerIdChanged, 
        hasValidAddressData, 
        checkAddressWithRetry,
        phoneNumbers,
        address,
        setFormData
    ]);

    // Carrega dados quando necessário
    useEffect(() => {
        if (prevCustomerIdRef.current !== selectedCustomerId) {
            setIsLoadingData(false);
            setAddressLoaded(true);
            prevCustomerIdRef.current = selectedCustomerId;
            return;
        }
        
        prevCustomerIdRef.current = selectedCustomerId;
        
        if (!initialData?.id || !selectedCustomerId) {
            setIsLoadingData(false);
            setAddressLoaded(true);
            return;
        }
        
        if (initialData?.id && selectedCustomerId) {
            loadEmployeeDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData?.id, selectedCustomerId]);

    // Handler de mudanças no formulário
    const handleFormChange = useCallback((e) => {
        const { name, type, checked, value } = e.target;
        
        if (type === 'checkbox') {
            setField(name, checked);
            return;
        }
        
        let maskedValue = value;
        if (name === 'document') {
            maskedValue = maskCPF(value);
        } else if (name === 'birthDate') {
            maskedValue = maskDate(value);
        }

        setField(name, maskedValue);
    }, [setField]);

    const runUserAccessAction = useCallback(async (action) => {
        const normalizedEmail = String(formData.email || '').trim();
        if (!normalizedEmail) {
            setUserAccessMessageModal({ open: true, title: 'Aviso', message: 'Este colaborador não possui e-mail cadastrado.' });
            return;
        }
        if (!isValidEmail(normalizedEmail)) {
            setUserAccessMessageModal({ open: true, title: 'Aviso', message: 'E-mail do colaborador é inválido.' });
            return;
        }

        setLoadingUserAccessAction(action);
        try {
            if (action === 'block') {
                await userService.blockByEmail(normalizedEmail);
                setUserAccessMessageModal({ open: true, title: 'Sucesso', message: 'Usuário bloqueado com sucesso.' });
            } else {
                await userService.unblockByEmail(normalizedEmail);
                setUserAccessMessageModal({ open: true, title: 'Sucesso', message: 'Usuário desbloqueado com sucesso.' });
            }
        } catch (err) {
            setUserAccessMessageModal({
                open: true,
                title: 'Erro',
                message: err?.message || `Erro ao ${action === 'block' ? 'bloquear' : 'desbloquear'} usuário.`,
            });
        } finally {
            setLoadingUserAccessAction(null);
        }
    }, [formData.email]);

    // Mostra loading durante carregamento de dados
    if (isEditing && (isLoadingData || !addressLoaded)) {
        return (
            <CrudForm
                title="Editar Colaborador"
                onSubmit={() => {}}
                onCancel={onCancel}
                isSubmitting={false}
            >
                <LoadingSpinner />
            </CrudForm>
        );
    }

    return (
        <CrudForm
            title={isEditing ? 'Editar Colaborador' : 'Novo Colaborador'}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            isSubmitting={isSubmitting}
        >
            <SectionTitle>Dados Pessoais</SectionTitle>
            
            <FormGroup>
                <label htmlFor="name">Nome Completo *</label>
                <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleFormChange}
                    $hasError={!!errors.name}
                />
                {errors.name && <span className="error">{errors.name}</span>}
            </FormGroup>

            <FormRow>
                <FormGroup>
                    <label htmlFor="email">Email *</label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        $hasError={!!errors.email}
                        disabled={isEditing}
                        title={isEditing ? 'Email não pode ser alterado' : ''}
                    />
                    {errors.email && <span className="error">{errors.email}</span>}
                </FormGroup>
                <FormGroup>
                    <label htmlFor="document">CPF</label>
                    <Input
                        id="document"
                        name="document"
                        type="text"
                        value={formData.document}
                        onChange={handleFormChange}
                        $hasError={!!errors.document}
                        placeholder="000.000.000-00"
                        maxLength={14}
                    />
                    {errors.document && <span className="error">{errors.document}</span>}
                </FormGroup>
            </FormRow>

            <FormRow>
                <FormGroup>
                    <label htmlFor="birthDate">Data de Nascimento</label>
                    <Input
                        id="birthDate"
                        name="birthDate"
                        type="text"
                        value={formData.birthDate}
                        onChange={handleFormChange}
                        $hasError={!!errors.birthDate}
                        placeholder="DD/MM/AAAA"
                        maxLength={10}
                    />
                    {errors.birthDate && <span className="error">{errors.birthDate}</span>}
                </FormGroup>
                <FormGroup>
                    <label htmlFor="jobTitle">Cargo</label>
                    <Input
                        id="jobTitle"
                        name="jobTitle"
                        type="text"
                        value={formData.jobTitle}
                        onChange={handleFormChange}
                        placeholder="Ex: Motorista"
                    />
                </FormGroup>
            </FormRow>

            <PhoneNumbersInput
                initialPhones={phoneNumbers.phoneNumbers}
                onChange={(phones) => {
                    phoneNumbers.setPhoneNumbers(phones);
                }}
            />

            <SectionTitle>Endereço</SectionTitle>
            <AddressForm
                initialAddress={address.address}
                onChange={(addr) => {
                    address.setAddress(addr);
                }}
                errors={{
                    zipCode: errors.zipCode,
                    street: errors.street,
                    number: errors.number,
                    complement: errors.complement,
                    district: errors.district,
                    city: errors.city,
                    stateAbbr: errors.stateAbbr,
                    addressName: errors.addressName,
                }}
                customerId={selectedCustomerId}
            />

            {isEditing && initialData?.id && (
                <RolesManager 
                    personId={initialData.id}
                    customerId={selectedCustomerId}
                />
            )}

            {isEditing && canManageUserBlock && initialData?.id && (
                <SystemAccessSection
                    email={formData.email}
                    loadingAction={loadingUserAccessAction}
                    onBlock={() => runUserAccessAction('block')}
                    onUnblock={() => runUserAccessAction('unblock')}
                />
            )}

            {!isEditing && (
                <>
                    <SectionTitle>Configurações</SectionTitle>
                    <CheckboxGroup>
                        <input
                            type="checkbox"
                            id="requester"
                            name="requester"
                            checked={formData.requester}
                            onChange={handleFormChange}
                        />
                        <label htmlFor="requester">É solicitante?</label>
                    </CheckboxGroup>

                    {canShowDriverField && (
                        <CheckboxGroup>
                            <input
                                type="checkbox"
                                id="driver"
                                name="driver"
                                checked={formData.driver}
                                onChange={handleFormChange}
                            />
                            <label htmlFor="driver">É motorista?</label>
                        </CheckboxGroup>
                    )}
                </>
            )}

            {errors.submit && (
                <MessageModal
                    title="Erro"
                    message={errors.submit}
                    onClose={() => setErrors((prev) => {
                        const next = { ...prev };
                        delete next.submit;
                        return next;
                    })}
                />
            )}

            {userAccessMessageModal.open && (
                <MessageModal
                    title={userAccessMessageModal.title}
                    message={userAccessMessageModal.message}
                    onClose={() => setUserAccessMessageModal((m) => ({ ...m, open: false }))}
                />
            )}
        </CrudForm>
    );
}
